import { Divider } from "@msuite/ui/divider";
import {
	Droppable,
	type DroppablePayloadBase,
	getDraggableType,
	getDroppablePayload,
} from "@msuite/ui/dnd";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { LazyFallback } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { cn } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { isNoteTextBlank } from "@msuite/utils/notes";
import type { NoteWithFiles } from "@msuite/utils/types";
import React, { lazy, Suspense } from "react";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";
import { UserCard } from "../cards";
import { NoteFiles } from "./note-files";
import { NoteMenu } from "./note-menu";
import { NoteText } from "./note-text";

const AuditModal = lazy(() => import("@/modals/audit-modal"));

/** Props Interface */
interface NoteProps {
	note: NoteWithFiles;
	onMarkAsReply: (noteId: string) => void;
	childNotes: NoteWithFiles[];
	isChildNote?: boolean;
	childLevel?: number;
	onCompleteEdit: () => void | Promise<void>;
	/** Whether the caller may write a note here at all — a reply is a note like any other. */
	canReply: boolean;
	withIsImportant?: boolean;
	isImportantLabel?: string;
	withIsImportantIsDisabled?: boolean;
}

export function Note({
	note,
	onMarkAsReply,
	childNotes,
	isChildNote,
	childLevel = 0,
	onCompleteEdit,
	canReply,
	withIsImportant,
	isImportantLabel,
	withIsImportantIsDisabled = false,
}: NoteProps) {
	/** Hooks */
	const childNotesDisclosure = useDisclosure({ defaultIsOpen: true });
	const { t } = useTranslation();
	const { citadel } = useCitadel();
	const { user } = useAuth();
	const auditModal = useDisclosure();

	/** Computed */
	// That a note was edited stays visible to everyone; what it said before does not. The history
	// behind this label is `GET /audit`, and `Notes.authorizeNoteParent` lets the author through
	// before it asks any rule — so a `read Note` test alone hides every member's own history, a
	// rule the base set deliberately omits. Someone else's note under a parent the caller may read
	// is still hidden here; that arm resolves through the parent server-side and has no client twin.
	const canReadHistory = note.createdBy === user?.id || citadel?.can("read", "Note", note) === true;

	/** Functions */
	function handleOnMarkAsReply() {
		onMarkAsReply(note.id);
	}

	/** Computed */
	const thisChildNotes = childNotes
		.filter((childNote) => childNote.parentNoteId === note.id)
		.toSorted((a, b) => {
			return a.createdAt.localeCompare(b.createdAt);
		});

	/** Functions */
	async function handleOnDrop(element: DroppablePayloadBase) {
		const type = getDraggableType(element);
		if (type === "note") {
			const { item } = getDroppablePayload<NoteWithFiles>(element);
			if (note.parentNoteId === item.id) return;
			if (note.id === item.parentNoteId) return;
			await client.notes[":noteId"].$patch({
				json: {
					note: item.note,
					noteId: item.id,
					parentNoteId: note.id,
				},
				param: {
					noteId: item.id,
				},
			});
			await onCompleteEdit();
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<Droppable
				accept={["note"]}
				acceptsSameOrigin={false}
				handleOnDrop={handleOnDrop}
			>
				<div
					id={note.id}
					className={cn("v-stack p-4", isChildNote && "border-t-[0.5px] border-color")}
					style={{
						paddingLeft: `${childLevel * 30 + 10}px`,
					}}
				>
					<div className="grow-0 justify-between items-center h-stack">
						<UserCard
							userId={note.createdBy}
							variant="plain"
						/>
						<div className="flex-1" />
						{isImportantLabel && note.isImportant && (
							<div className="pl-4 h-stack">
								<Icon
									name="Check"
									className="stroke-emerald-500 text-xs"
								/>
								<p className="label">{isImportantLabel}</p>
								<p className="label">&#183;</p>
							</div>
						)}
						<p className="label">
							{moment(note.createdAt).calendar()}
							{note.updatedAt ? (
								<span
									className={cn(canReadHistory && "cursor-pointer clickable-opacity")}
									onClick={canReadHistory ? auditModal.onOpen : undefined}
								>{` (${t("notes.edited")})`}</span>
							) : (
								""
							)}
						</p>
						<NoteMenu
							handleOnMarkAsReply={handleOnMarkAsReply}
							onCompleteEdit={onCompleteEdit}
							note={note}
							canReply={canReply}
							withIsImportant={withIsImportant}
							isImportantLabel={isImportantLabel}
							withIsImportantIsDisabled={withIsImportantIsDisabled}
						/>
					</div>
					{/* A note may be an attachment and nothing else, and `NoteText` falls back to the
					    `--` placeholder rather than to nothing — right in a data grid cell, wrong
					    above a photograph that is the whole message. */}
					{!isNoteTextBlank(note.note) && (
						<NoteText
							note={note.note}
							className="col-start-2 mr-4 static"
							withPopover={false}
						/>
					)}
					<NoteFiles
						note={note}
						onCompleteEdit={onCompleteEdit}
					/>
					{thisChildNotes.length > 0 && !childNotesDisclosure.isOpen && (
						<div
							className="col-start-2 clickable-opacity"
							onClick={childNotesDisclosure.onToggle}
						>
							<p className="text-xs">
								{thisChildNotes.length} {t("notes.answers")}
							</p>
						</div>
					)}
				</div>
			</Droppable>
			{thisChildNotes.length > 0 && childNotesDisclosure.isOpen && (
				<React.Fragment>
					{thisChildNotes?.map((childNote) => (
						<Note
							key={childNote.id}
							note={childNote}
							onMarkAsReply={onMarkAsReply}
							childNotes={childNotes}
							isChildNote
							childLevel={childLevel + 1}
							onCompleteEdit={onCompleteEdit}
							canReply={canReply}
						/>
					))}
				</React.Fragment>
			)}
			{!isChildNote && <Divider />}
			{auditModal.isOpen && (
				<Suspense fallback={<LazyFallback variant="portal" />}>
					<AuditModal
						isOpen={auditModal.isOpen}
						onClose={auditModal.onClose}
						auditTable="notesAudit"
						queryKey={["notesAudit", note.id]}
						identifier={{
							key: "noteId",
							value: note.id,
						}}
					/>
				</Suspense>
			)}
		</React.Fragment>
	);
}
